
class PresenceRequestManager {
  private activeRequests = new Map<string, Promise<any>>();
  private lastFetchTimes = new Map<string, number>();
  private requestCounts = new Map<string, number>();
  private readonly MIN_FETCH_INTERVAL = 10000; // 10 seconds for non-editing
  private readonly EDITING_FETCH_INTERVAL = 2000; // 2 seconds for editing
  private readonly MAX_REQUESTS_PER_MINUTE = 30;

  // Deduplicate simultaneous requests
  async deduplicate<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    if (this.activeRequests.has(key)) {
      return this.activeRequests.get(key) as Promise<T>;
    }

    const promise = requestFn().finally(() => {
      this.activeRequests.delete(key);
    });

    this.activeRequests.set(key, promise);
    return promise;
  }

  // Check if fetch is allowed based on timing and rate limits
  canFetch(key: string, hasEditingUsers: boolean = false): boolean {
    const now = Date.now();
    const lastFetch = this.lastFetchTimes.get(key) || 0;
    const interval = hasEditingUsers ? this.EDITING_FETCH_INTERVAL : this.MIN_FETCH_INTERVAL;
    
    // Check timing interval
    if (now - lastFetch < interval) {
      return false;
    }

    // Check rate limiting
    const currentMinute = Math.floor(now / 60000);
    const requestKey = `${key}-${currentMinute}`;
    const currentCount = this.requestCounts.get(requestKey) || 0;
    
    if (currentCount >= this.MAX_REQUESTS_PER_MINUTE) {
      console.warn(`🚫 [PRESENCE MANAGER] Rate limit exceeded for ${key}`);
      return false;
    }

    return true;
  }

  // Mark fetch as completed
  markFetch(key: string): void {
    const now = Date.now();
    this.lastFetchTimes.set(key, now);
    
    const currentMinute = Math.floor(now / 60000);
    const requestKey = `${key}-${currentMinute}`;
    const currentCount = this.requestCounts.get(requestKey) || 0;
    this.requestCounts.set(requestKey, currentCount + 1);
    
    // Cleanup old entries
    this.cleanup();
  }

  // Cleanup old tracking data
  private cleanup(): void {
    const now = Date.now();
    const currentMinute = Math.floor(now / 60000);
    
    // Remove entries older than 5 minutes
    for (const [key, value] of this.requestCounts.entries()) {
      const keyMinute = parseInt(key.split('-').pop() || '0');
      if (currentMinute - keyMinute > 5) {
        this.requestCounts.delete(key);
      }
    }
    
    // Remove fetch times older than 1 hour
    for (const [key, time] of this.lastFetchTimes.entries()) {
      if (now - time > 3600000) {
        this.lastFetchTimes.delete(key);
      }
    }
  }

  // Get cache age for a key
  getCacheAge(key: string): number {
    const lastFetch = this.lastFetchTimes.get(key) || 0;
    return Date.now() - lastFetch;
  }
}

export const presenceRequestManager = new PresenceRequestManager();
